import React, { useEffect } from 'react';
import useScrollLock from '../../../hooks/useScrollLock';
import { GoClockFill } from 'react-icons/go';
import { BsFillCalendarDateFill, BsPersonWalking } from 'react-icons/bs';
import { FaClipboardList } from 'react-icons/fa';
import { HiMiniUsers } from 'react-icons/hi2';
import { PiUsersFourFill } from 'react-icons/pi';
import { MdExpandCircleDown } from 'react-icons/md';
import { IoReloadCircle } from 'react-icons/io5';
import { RiUser5Fill, RiSearchEyeLine } from 'react-icons/ri';
import { IoClose } from 'react-icons/io5';

interface Session {
  id: string;
  type: 'solo' | 'team';
  goal: string;
  duration: number;
  completedAt: string;
  notes: string;
  members: string[];
  xp: number;
  music?: string;
}

interface AllSessionsModalProps {
  open: boolean;
  onClose: () => void;
  allSessions: Session[];
  filteredSessions: Session[];
  sessionSearch: string;
  setSessionSearch: React.Dispatch<React.SetStateAction<string>>;
  sessionFilter: 'all' | 'solo' | 'team';
  setSessionFilter: React.Dispatch<React.SetStateAction<'all' | 'solo' | 'team'>>;
  expandedSessions: Record<string, boolean>;
  setExpandedSessions: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  onViewDetails: (session: Session) => void;
  onRepeatSession: (session: Session) => void;
}

// Helper function to format duration
// Helper function to format duration
const formatDuration = (durationInMinutes: number): string => {
  const mins = Math.floor(durationInMinutes);
  const secs = Math.round((durationInMinutes - mins) * 60);

  if (mins === 0 && secs === 0) return '0s';
  if (mins === 0) return `${secs}s`;
  if (secs === 0) return `${mins}m`;
  return `${mins}m ${secs}s`;
};

const AllSessionsModal: React.FC<AllSessionsModalProps> = ({
  open,
  onClose,
  allSessions,
  filteredSessions,
  sessionSearch,
  setSessionSearch,
  sessionFilter,
  setSessionFilter,
  expandedSessions,
  setExpandedSessions,
  onViewDetails,
  onRepeatSession,
}) => {
  // Prevent body scroll when modal is open
  useScrollLock(open);

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[#FFF8F0] rounded-2xl p-8 max-w-6xl w-full max-h-[90vh] overflow-hidden border border-gray-200/60 shadow-lg flex flex-col">
        {/* Modal Header */}
        <div className="flex justify-between items-start mb-6">
          <div className="space-y-1">
            <h2 className="text-3xl font-medium text-gray-900">
              All Sessions
            </h2>
            <p className="text-base text-gray-600 font-light">Your complete focus journey</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl font-light transition-colors"
          >
            <IoClose />
          </button>
        </div>

        {/* Search and Filter Controls */}
        <div className="mb-6 space-y-4">
          {/* Search Bar */}
          <div className="relative">
            <input
              type="text"
              value={sessionSearch}
              onChange={(e) => setSessionSearch(e.target.value)}
              placeholder="Search sessions by goal or notes..."
              className="w-full px-5 py-4 bg-white border border-gray-200/60 rounded-2xl text-gray-900 placeholder:text-gray-400 focus:ring-1 focus:ring-orange-400/30 focus:border-orange-300/50 focus:outline-none transition-all duration-300 text-base font-normal"
            />
            <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400">
              <RiSearchEyeLine className="w-5 h-5" />
            </div>
          </div>

          {/* Filter Buttons */}
          <div className="flex space-x-3">
            {[
              { id: 'all', label: 'All Sessions', count: allSessions.length },
              { id: 'solo', label: 'Solo', count: allSessions.filter(s => s.type === 'solo').length },
              { id: 'team', label: 'Team', count: allSessions.filter(s => s.type === 'team').length }
            ].map((filter) => (
              <button
                key={filter.id}
                onClick={() => setSessionFilter(filter.id as 'all' | 'solo' | 'team')}
                className={`px-5 py-2.5 rounded-full font-normal transition-all duration-300 text-sm ${sessionFilter === filter.id
                  ? 'bg-orange-50 text-orange-600 border border-orange-200/50'
                  : 'bg-white text-gray-600 border border-gray-200/40 hover:border-orange-200/60 hover:text-orange-600/80'
                  }`}
              >
                {filter.label} ({filter.count})
              </button>
            ))}
          </div>
        </div>

        {/* Sessions List */}
        <div className="flex-1 overflow-y-auto space-y-0 pr-2 pb-4 min-h-0">
          {filteredSessions.length > 0 ? (
            filteredSessions.map((session, index) => (
              <div key={session.id}>
                <div
                  className="py-4 px-4 hover:bg-white/50 transition-colors cursor-pointer"
                  onClick={() => setExpandedSessions(prev => ({
                    ...prev,
                    [session.id]: !prev[session.id]
                  }))}
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      {/* Session Type Tag */}
                      <div className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1.5 flex-shrink-0 ${session.type === 'solo'
                        ? 'bg-orange-50 text-orange-600 border border-orange-200/50'
                        : 'bg-orange-50 text-orange-600 border border-orange-200/50'
                        }`}>
                        {session.type === 'solo' ? (
                          <>
                            <BsPersonWalking className="text-sm" />
                            <span>Solo</span>
                          </>
                        ) : (
                          <>
                            <PiUsersFourFill className="text-sm" />
                            <span>Team</span>
                          </>
                        )}
                      </div>

                      {/* Session Info */}
                      <div className="flex-1 min-w-0">
                        <h4 className="text-base font-medium text-gray-900 mb-1 truncate">{session.goal}</h4>
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span className="flex items-center gap-1.5">
                            <GoClockFill className="text-orange-500" /> {formatDuration(session.duration)}
                          </span>
                          <span className="flex items-center gap-1.5">
                            <BsFillCalendarDateFill className="text-orange-500" /> {new Date(session.completedAt).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Expand/Collapse Icon */}
                    <div className="text-gray-400 flex-shrink-0">
                      <MdExpandCircleDown
                        className={`w-5 h-5 transition-transform duration-300 ${expandedSessions[session.id] ? 'rotate-180' : ''}`}
                      />
                    </div>
                  </div>
                </div>

                {/* Expanded Content */}
                {expandedSessions[session.id] && (
                  <div className="px-4 pb-4 border-t border-gray-200/40">
                    <div className="pt-4 space-y-4">
                      {session.notes && (
                        <div>
                          <h5 className="text-sm font-medium text-gray-700 mb-2">Session Notes</h5>
                          <p className="text-sm text-gray-600 leading-relaxed">{session.notes}</p>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => onViewDetails(session)}
                          className="px-4 py-2 bg-white border border-orange-200/50 text-orange-600 rounded-lg text-sm font-normal hover:bg-orange-50/50 transition-colors flex items-center gap-2"
                        >
                          <FaClipboardList /> View Details
                        </button>
                        <button
                          onClick={() => onRepeatSession(session)}
                          className="px-4 py-2 bg-white border border-orange-200/50 text-orange-600 rounded-lg text-sm font-normal hover:bg-orange-50/50 transition-colors flex items-center gap-2"
                        >
                          <IoReloadCircle /> Repeat Session <span className="text-orange-500/70">({formatDuration(session.duration)})</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {index < filteredSessions.length - 1 && (
                  <div className="border-b border-gray-200/40"></div>
                )}
              </div>
            ))
          ) : (
            <div className="text-center py-12">
              <div className="text-4xl mb-4">🔍</div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">No sessions found</h3>
              <p className="text-gray-600 font-light">Try adjusting your search or filter criteria</p>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="mt-6 pt-6 border-t border-gray-200/40 flex justify-between items-center">
          <div className="text-sm text-gray-500 font-light">
            Showing {filteredSessions.length} of {allSessions.length} sessions
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => {
                setSessionSearch('');
                setSessionFilter('all');
              }}
              className="px-4 py-2 bg-white border border-gray-200/60 text-gray-600 rounded-lg text-sm font-normal hover:border-gray-300/60 transition-colors"
            >
              Clear Filters
            </button>
            <button
              onClick={onClose}
              className="px-6 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AllSessionsModal;
